import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  Button,
  Chip,
  Divider,
  IconButton,
  ButtonBase,
} from '@mui/material';
import {
  Close as CloseIcon,
  Check as CheckIcon,
  Person as EmployeeIcon,
  Engineering as ContractorIcon,
  Apartment as TenantIcon,
  Groups as VisitorIcon,
  PersonAdd as ManualAddIcon,
  Badge as BadgeDataIcon,
  Assignment as LobbyTrackIcon,
  EventBusy as NoBadgeIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { appColors } from '../../Config/Theme';
import type { AlphaRange } from '../../Utils/alphaRangeHelpers';
import { PersonType } from '../../Types/enums';
import type { SourceFilter } from '../../Utils/personHelpers';
import TenantCompanyFilterSection from './TenantCompanyFilterSection';
import type { TenantCompanyOption } from '../../Utils/tenantCompanyHelpers';
import { formatTenantCompanyChipLabel } from '../../Utils/tenantCompanyHelpers';

interface HeadcountFilterDrawerProps {
  open: boolean;
  onClose: () => void;
  alphaFilter: AlphaRange;
  onAlphaFilterChange: (v: AlphaRange) => void;
  personTypeFilters: string[];
  onPersonTypeFiltersChange: (v: string[]) => void;
  rosterContextFilters: SourceFilter[];
  onRosterContextFiltersChange: (v: SourceFilter[]) => void;
  onClearAll: () => void;
  /** Optional counts for person type options */
  personTypeCounts?: Record<string, number>;
  /** Optional counts for roster context options */
  rosterContextCounts?: Record<string, number>;
  /** Optional: visible filtered count for "Show N people" footer */
  filteredCount?: number;
  /** Called when user clicks the "Assigned Office / No Badge Today" scope option */
  onAssignedOfficeNoBadgeScopeSwitch?: () => void;
  /** True when roster scope is currently assignedOfficeNoBadge */
  isAssignedOfficeNoBadgeScopeActive?: boolean;
  /** Tenant company filter state */
  tenantCompanyFilters: string[];
  onTenantCompanyFiltersChange: (v: string[]) => void;
  /** Tenant company options with counts, pre-sorted */
  tenantCompanyOptions: TenantCompanyOption[];
  /** Total count of tenant people in the roster */
  totalTenantCount: number;
}

const PERSON_TYPE_OPTIONS: { label: string; value: string; icon: React.ReactNode }[] = [
  { label: 'Employees', value: PersonType.Employee, icon: <EmployeeIcon sx={{ fontSize: 18 }} /> },
  { label: 'Contractors', value: PersonType.Contractor, icon: <ContractorIcon sx={{ fontSize: 18 }} /> },
  { label: 'Visitors', value: PersonType.Visitor, icon: <VisitorIcon sx={{ fontSize: 18 }} /> },
  { label: 'Tenants', value: PersonType.Tenant, icon: <TenantIcon sx={{ fontSize: 18 }} /> },
  { label: 'Manual Add', value: PersonType.Unlisted, icon: <ManualAddIcon sx={{ fontSize: 18 }} /> },
];

const ROSTER_CONTEXT_OPTIONS: { label: string; secondaryLabel?: string; value: SourceFilter; icon: React.ReactNode; fullWidth?: boolean }[] = [
  { label: 'Badged In', value: 'badgeData', icon: <BadgeDataIcon sx={{ fontSize: 18 }} /> },
  { label: 'Lobby Track', value: 'lobbyTrack', icon: <LobbyTrackIcon sx={{ fontSize: 18 }} /> },
  { label: 'Manual Add', value: 'manualAdd', icon: <ManualAddIcon sx={{ fontSize: 18 }} /> },
];

/** Person type label map for active filter chips */
const PERSON_TYPE_LABELS: Record<string, string> = {
  [PersonType.Employee]: 'Employees',
  [PersonType.Contractor]: 'Contractors',
  [PersonType.Tenant]: 'Tenants',
  [PersonType.Visitor]: 'Visitors',
  [PersonType.Unlisted]: 'Manual Add',
};

/** Roster context label map for active filter chips */
const ROSTER_CONTEXT_LABELS: Record<string, string> = {
  badgeData: 'Badged In',
  lobbyTrack: 'Lobby Track',
  manualAdd: 'Manual Add',
};

const HeadcountFilterDrawer: React.FC<HeadcountFilterDrawerProps> = ({
  open,
  onClose,
  alphaFilter,
  onAlphaFilterChange,
  personTypeFilters,
  onPersonTypeFiltersChange,
  rosterContextFilters,
  onRosterContextFiltersChange,
  onClearAll,
  personTypeCounts,
  rosterContextCounts,
  filteredCount,
  onAssignedOfficeNoBadgeScopeSwitch,
  isAssignedOfficeNoBadgeScopeActive,
  tenantCompanyFilters,
  onTenantCompanyFiltersChange,
  tenantCompanyOptions,
  totalTenantCount,
}) => {
  const togglePersonType = (value: string) => {
    onPersonTypeFiltersChange(
      personTypeFilters.includes(value)
        ? personTypeFilters.filter((v) => v !== value)
        : [...personTypeFilters, value],
    );
  };

  const toggleRosterContext = (value: SourceFilter) => {
    onRosterContextFiltersChange(
      rosterContextFilters.includes(value)
        ? rosterContextFilters.filter((v) => v !== value)
        : [...rosterContextFilters, value],
    );
  };

  const hasAnyFilters =
    personTypeFilters.length > 0 || rosterContextFilters.length > 0 || tenantCompanyFilters.length > 0;

  const activeFilterCount = personTypeFilters.length + rosterContextFilters.length + tenantCompanyFilters.length;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: { xs: '100%', sm: 380 },
            maxWidth: '100vw',
            p: 0,
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      {/* ── Sticky Header ── */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          bgcolor: appColors.surface.card,
          px: 2.5,
          pt: 2.5,
          pb: 2,
          borderBottom: `1px solid ${appColors.border.default}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterIcon sx={{ fontSize: 20, color: appColors.primary.main }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: appColors.text.heading, fontSize: '1.1rem' }}>
              {hasAnyFilters ? `Filters · ${activeFilterCount} active` : 'Filters'}
            </Typography>
          </Box>
          <IconButton onClick={onClose} aria-label="Close filters" size="small" sx={{ color: appColors.text.body }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="body2" sx={{ color: appColors.text.caption, mt: 0.5, fontSize: '0.8rem' }}>
          Narrow the roster by person type and roster context.
        </Typography>
      </Box>

      {/* ── Scrollable Content ── */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 2.5, py: 2.5 }}>
        {/* ── Active Filter Chips ── */}
        {hasAnyFilters && (
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: appColors.text.caption, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1, display: 'block' }}>
              Selected
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
              {personTypeFilters.map((pt) => (
                <Chip
                  key={`active-pt-${pt}`}
                  size="small"
                  label={PERSON_TYPE_LABELS[pt] ?? pt}
                  onDelete={() => togglePersonType(pt)}
                  deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                  aria-label={`Remove ${PERSON_TYPE_LABELS[pt] ?? pt} filter`}
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    bgcolor: `${appColors.primary.main}14`,
                    color: appColors.primary.main,
                    border: `1px solid ${appColors.primary.main}40`,
                    '& .MuiChip-deleteIcon': { color: appColors.primary.main },
                  }}
                />
              ))}
              {rosterContextFilters.map((rc) => (
                <Chip
                  key={`active-rc-${rc}`}
                  size="small"
                  label={ROSTER_CONTEXT_LABELS[rc] ?? rc}
                  onDelete={() => toggleRosterContext(rc)}
                  deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                  aria-label={`Remove ${ROSTER_CONTEXT_LABELS[rc] ?? rc} filter`}
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    bgcolor: `${appColors.primary.main}14`,
                    color: appColors.primary.main,
                    border: `1px solid ${appColors.primary.main}40`,
                    '& .MuiChip-deleteIcon': { color: appColors.primary.main },
                  }}
                />
              ))}
              {tenantCompanyFilters.map((tc) => {
                const opt = tenantCompanyOptions.find((o) => o.key === tc);
                const chipLabel = formatTenantCompanyChipLabel(opt?.label ?? tc);
                return (
                  <Chip
                    key={`active-tc-${tc}`}
                    size="small"
                    label={chipLabel}
                    onDelete={() =>
                      onTenantCompanyFiltersChange(tenantCompanyFilters.filter((v) => v !== tc))
                    }
                    deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                    aria-label={`Remove ${chipLabel} filter`}
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      bgcolor: `${appColors.primary.main}14`,
                      color: appColors.primary.main,
                      border: `1px solid ${appColors.primary.main}40`,
                      '& .MuiChip-deleteIcon': { color: appColors.primary.main },
                    }}
                  />
                );
              })}
              <Button
                size="small"
                onClick={onClearAll}
                sx={{ fontSize: '0.7rem', fontWeight: 600, minHeight: 24, textTransform: 'none', color: appColors.text.caption, ml: 0.5 }}
              >
                Clear all
              </Button>
            </Box>
          </Box>
        )}

        {/* ── Person Type ── */}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: appColors.text.heading, mb: 1.25, fontSize: '0.875rem' }}>
          Person Type
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.25, mb: 3 }}>
          {PERSON_TYPE_OPTIONS.map((opt) => {
            const isActive = personTypeFilters.includes(opt.value);
            const count = personTypeCounts?.[opt.value] ?? 0;
            const isZero = personTypeCounts != null && count === 0;
            return (
              <ButtonBase
                key={opt.value}
                onClick={() => !isZero && togglePersonType(opt.value)}
                disabled={isZero}
                aria-pressed={isActive}
                aria-label={`${opt.label}${count != null ? `, ${count}` : ''}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.5,
                  py: 1.25,
                  minHeight: 48,
                  borderRadius: '10px',
                  border: `1.5px solid ${isActive ? appColors.primary.main : isZero ? appColors.border.light : appColors.border.default}`,
                  bgcolor: isActive ? appColors.primary.main : isZero ? appColors.surface.inactive : appColors.surface.card,
                  opacity: isZero ? 0.55 : 1,
                  cursor: isZero ? 'default' : 'pointer',
                  transition: 'all 0.15s ease',
                  '&:hover': !isZero ? {
                    bgcolor: isActive ? appColors.primary.dark : `${appColors.primary.main}08`,
                    borderColor: isActive ? appColors.primary.dark : appColors.primary.main,
                  } : {},
                  '&:focus-visible': { outline: `2px solid ${appColors.primary.main}`, outlineOffset: 2 },
                }}
              >
                <Box sx={{ color: isActive ? '#FFFFFF' : isZero ? appColors.text.disabled : appColors.text.caption, display: 'flex', flexShrink: 0 }}>
                  {opt.icon}
                </Box>
                <Box sx={{ flex: 1, textAlign: 'left' }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.82rem',
                      color: isActive ? '#FFFFFF' : isZero ? appColors.text.disabled : appColors.text.heading,
                      lineHeight: 1.3,
                    }}
                  >
                    {opt.label}
                  </Typography>
                </Box>
                {personTypeCounts != null && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      color: isActive ? 'rgba(255,255,255,0.85)' : isZero ? appColors.text.disabled : appColors.text.caption,
                      minWidth: 20,
                      textAlign: 'right',
                    }}
                  >
                    {count}
                  </Typography>
                )}
                {isActive && (
                  <CheckIcon sx={{ fontSize: 16, color: '#FFFFFF', flexShrink: 0, ml: 0.25 }} />
                )}
              </ButtonBase>
            );
          })}
        </Box>

        <Divider sx={{ mb: 2.5 }} />

        {/* ── Tenant Company ── */}
        {totalTenantCount > 0 && (
          <>
            <TenantCompanyFilterSection
              tenantCompanyOptions={tenantCompanyOptions}
              selectedTenantCompanies={tenantCompanyFilters}
              tenantsSelected={personTypeFilters.includes(PersonType.Tenant)}
              totalTenantCount={totalTenantCount}
              onToggleCompany={(key) => {
                onTenantCompanyFiltersChange(
                  tenantCompanyFilters.includes(key)
                    ? tenantCompanyFilters.filter((v) => v !== key)
                    : [...tenantCompanyFilters, key],
                );
              }}
              onSelectAllTenantCompanies={() => {
                onTenantCompanyFiltersChange([]);
                if (!personTypeFilters.includes(PersonType.Tenant)) {
                  onPersonTypeFiltersChange([...personTypeFilters, PersonType.Tenant]);
                }
              }}
              onClearTenantCompanies={() => onTenantCompanyFiltersChange([])}
            />
            <Divider sx={{ my: 2.5 }} />
          </>
        )}

        {/* ── Roster Context ── */}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: appColors.text.heading, mb: 0.5, fontSize: '0.875rem' }}>
          Roster Context
        </Typography>
        <Typography variant="caption" sx={{ color: appColors.text.caption, display: 'block', mb: 1.5, fontSize: '0.75rem' }}>
          How the person was included in the active accountability roster.
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.25, mb: 2 }}>
          {ROSTER_CONTEXT_OPTIONS.map((opt) => {
            const isActive = rosterContextFilters.includes(opt.value);
            const count = rosterContextCounts?.[opt.value] ?? 0;
            const isZero = rosterContextCounts != null && count === 0;
            const handleClick = () => {
              if (isZero) return;
              toggleRosterContext(opt.value);
            };
            return (
              <ButtonBase
                key={opt.value}
                onClick={handleClick}
                disabled={isZero}
                aria-pressed={isActive}
                aria-label={`${opt.label}${opt.secondaryLabel ? ` ${opt.secondaryLabel}` : ''}${rosterContextCounts != null ? `, ${count}` : ''}`
                }
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.5,
                  py: 1.25,
                  minHeight: 48,
                  borderRadius: '10px',
                  border: `1.5px solid ${isActive ? appColors.primary.main : isZero ? appColors.border.light : appColors.border.default}`,
                  bgcolor: isActive ? appColors.primary.main : isZero ? appColors.surface.inactive : appColors.surface.card,
                  opacity: isZero ? 0.55 : 1,
                  cursor: isZero ? 'default' : 'pointer',
                  transition: 'all 0.15s ease',
                  gridColumn: opt.fullWidth ? '1 / -1' : undefined,
                  '&:hover': !isZero ? {
                    bgcolor: isActive ? appColors.primary.dark : `${appColors.primary.main}08`,
                    borderColor: isActive ? appColors.primary.dark : appColors.primary.main,
                  } : {},
                  '&:focus-visible': { outline: `2px solid ${appColors.primary.main}`, outlineOffset: 2 },
                }}
              >
                <Box sx={{ color: isActive ? '#FFFFFF' : isZero ? appColors.text.disabled : appColors.text.caption, display: 'flex', flexShrink: 0 }}>
                  {opt.icon}
                </Box>
                <Box sx={{ flex: 1, textAlign: 'left' }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.82rem',
                      color: isActive ? '#FFFFFF' : isZero ? appColors.text.disabled : appColors.text.heading,
                      lineHeight: 1.3,
                    }}
                  >
                    {opt.label}
                  </Typography>
                  {opt.secondaryLabel && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '0.7rem',
                        color: isActive ? 'rgba(255,255,255,0.75)' : isZero ? appColors.text.disabled : appColors.text.caption,
                        lineHeight: 1.2,
                      }}
                    >
                      {opt.secondaryLabel}
                    </Typography>
                  )}
                </Box>
                {rosterContextCounts != null && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      color: isActive ? 'rgba(255,255,255,0.85)' : isZero ? appColors.text.disabled : appColors.text.caption,
                      minWidth: 20,
                      textAlign: 'right',
                    }}
                  >
                    {count}
                  </Typography>
                )}
                {isActive && (
                  <CheckIcon sx={{ fontSize: 16, color: '#FFFFFF', flexShrink: 0, ml: 0.25 }} />
                )}
              </ButtonBase>
            );
          })}
        </Box>

        {/* ── Other Roster Scopes ── */}
        {onAssignedOfficeNoBadgeScopeSwitch && (() => {
          const noBadgeCount = rosterContextCounts?.['assignedOfficeNoBadge'] ?? 0;
          const noBadgeZero = rosterContextCounts != null && noBadgeCount === 0;
          return (
            <>
              <Divider sx={{ mb: 2.5 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: appColors.text.heading, mb: 0.5, fontSize: '0.875rem' }}>
                Other Roster Scopes
              </Typography>
              <Typography variant="caption" sx={{ color: appColors.text.caption, display: 'block', mb: 1.5, fontSize: '0.75rem' }}>
                Assigned-office employees without badge activity are outside the default accountability roster.
              </Typography>
              <ButtonBase
                onClick={() => !noBadgeZero && onAssignedOfficeNoBadgeScopeSwitch()}
                disabled={noBadgeZero}
                aria-pressed={!!isAssignedOfficeNoBadgeScopeActive}
                aria-label={`Show Assigned Office / No Badge Today, ${noBadgeCount} people`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.5,
                  py: 1.25,
                  minHeight: 48,
                  borderRadius: '10px',
                  width: '100%',
                  border: `1.5px solid ${isAssignedOfficeNoBadgeScopeActive ? appColors.primary.main : noBadgeZero ? appColors.border.light : appColors.border.default}`,
                  bgcolor: isAssignedOfficeNoBadgeScopeActive ? appColors.primary.main : noBadgeZero ? appColors.surface.inactive : appColors.surface.card,
                  opacity: noBadgeZero ? 0.55 : 1,
                  cursor: noBadgeZero ? 'default' : 'pointer',
                  transition: 'all 0.15s ease',
                  '&:hover': !noBadgeZero ? {
                    bgcolor: isAssignedOfficeNoBadgeScopeActive ? appColors.primary.dark : `${appColors.primary.main}08`,
                    borderColor: isAssignedOfficeNoBadgeScopeActive ? appColors.primary.dark : appColors.primary.main,
                  } : {},
                  '&:focus-visible': { outline: `2px solid ${appColors.primary.main}`, outlineOffset: 2 },
                }}
              >
                <Box sx={{ color: isAssignedOfficeNoBadgeScopeActive ? '#FFFFFF' : noBadgeZero ? appColors.text.disabled : appColors.text.caption, display: 'flex', flexShrink: 0 }}>
                  <NoBadgeIcon sx={{ fontSize: 18 }} />
                </Box>
                <Box sx={{ flex: 1, textAlign: 'left' }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.82rem',
                      color: isAssignedOfficeNoBadgeScopeActive ? '#FFFFFF' : noBadgeZero ? appColors.text.disabled : appColors.text.heading,
                      lineHeight: 1.3,
                    }}
                  >
                    Assigned Office / No Badge Today
                  </Typography>
                </Box>
                {rosterContextCounts != null && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      color: isAssignedOfficeNoBadgeScopeActive ? 'rgba(255,255,255,0.85)' : noBadgeZero ? appColors.text.disabled : appColors.text.caption,
                      minWidth: 20,
                      textAlign: 'right',
                    }}
                  >
                    {noBadgeCount}
                  </Typography>
                )}
                {isAssignedOfficeNoBadgeScopeActive && (
                  <CheckIcon sx={{ fontSize: 16, color: '#FFFFFF', flexShrink: 0, ml: 0.25 }} />
                )}
              </ButtonBase>
            </>
          );
        })()}
      </Box>

      {/* ── Sticky Footer ── */}
      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          bgcolor: appColors.surface.card,
          px: 2.5,
          py: 2,
          borderTop: `1px solid ${appColors.border.default}`,
          boxShadow: '0 -2px 8px rgba(0,0,0,0.04)',
          display: 'flex',
          gap: 2,
        }}
      >
        <Button
          variant="outlined"
          onClick={onClearAll}
          disabled={!hasAnyFilters}
          sx={{ flex: 1, minHeight: 44, fontWeight: 600, textTransform: 'none' }}
        >
          Clear all
        </Button>
        <Button
          variant="contained"
          onClick={onClose}
          sx={{ flex: 1, minHeight: 44, fontWeight: 600, textTransform: 'none' }}
        >
          {filteredCount != null ? `Show ${filteredCount} people` : 'Done'}
        </Button>
      </Box>
    </Drawer>
  );
};

export default React.memo(HeadcountFilterDrawer);
